// @file Handles error reporting
import { browserCan, browserIs } from '@@/bits/browser'
import window from '@@/bits/global'
import { networkMonitor } from '@@/bits/network_monitor'
import type { FetchError } from '@padlet/fetch'
import { CaptureConsole, Dedupe, ExtraErrorData } from '@sentry/integrations'
import type { Event, EventHint, ScopeContext } from '@sentry/types'
import type { BrowserOptions, StackFrame } from '@sentry/vue'
import {
  captureException as sentryCaptureException,
  configureScope,
  init as sentryInit,
  Integrations,
  withScope,
} from '@sentry/vue'

const API_KEY = 'https://7fbb2288ec794bef8ae6cad3689c63ba@o253203.ingest.sentry.io/22080'
const PROD_ENV = 'production'
const DEV_ENV = 'development'
const STAGING_ENV = 'staging'
// Sentry hijacks all console errors sometimes. Turning this on and off allows us to debug.
const ENABLED = true
const SANDBOX_SOURCE_TAG_VALUE = 'sandbox'
const MAX_NETWORK_FAILURES_PER_SOURCE = 3
const MAX_NETWORK_FAILURES_PER_SESSION = 15
const MAX_RECOVERIES_PER_SOURCE = 3
const MAX_RECOVERIES_PER_SESSION = 15
const NETWORK_FAILURE_SESSION_SAMPLE_RATE = 0.01
const isNetworkFailureSampledSession = Math.random() < NETWORK_FAILURE_SESSION_SAMPLE_RATE
const networkFailureCountsBySource = new Map<string, number>()
let networkFailureCount = 0
const recoveryCountsBySource = new Map<string, number>()
let recoveryCount = 0

function isSandboxPage(): boolean {
  return document?.documentElement?.dataset?.layout === 'whiteboard'
}

interface ScopeOptions {
  cookies?: string
  locale?: string
  userId?: number
  fetchingMode?: string
  // Identifies the originating product surface for downstream Sentry filtering.
  // Auto-detected for Sandbox pages via the `data-layout` attribute on `<html>`.
  source?: string
}

interface InitOptions extends ScopeOptions {
  vue?: any
  environment?: string
  release?: string
}

interface CaptureOptions {
  level?: string
  context?: any
  tags?: ScopeContext['tags']
  fingerprint?: string[] // https://docs.sentry.io/platforms/javascript/enriching-events/fingerprinting/
  networkFailure?: boolean
  source?: string
}

const allowUrls = [/padlet\.net/]

// Ignore specific errors and only send events for URLs that we care about
// @see https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
const ignoreErrors = []
const denyUrls = [
  // Some users save the Padlet page and load it on their file system. We ignore these errors
  /^file:\/\/\//i,
]

const messagesToExclude: string[] = [
  // Mobile Safari and iOS throw this for some reason we don't yet understand.
  // https://sentry.io/organizations/padlet-y9/issues/1050542665/events/42380bd67ff447798e402cd9121562c4/?project=22080
  'WebKit encountered an internal error',
  // Mobile Safari and iOS throw this for some reason we don't yet understand.
  // https://sentry.io/organizations/padlet-y9/issues/1050542665/events/f4ca2db73b0b46d1af66b5c50af14a7c/?project=22080
  'Software caused connection abort',
  // Mobile Safari and iOS throw this for some reason we don't yet understand.
  // https://sentry.io/organizations/padlet-y9/issues/1050542665/events/37ea064939a246fb990678403387dd90/?project=22080
  'The network connection was lost',
  // https://sentry.io/organizations/padlet-y9/issues/1303001659/?project=22080
  'Non-Error promise rejection captured with keys',
  // https://padlet-y9.sentry.io/share/issue/6fbfdc62161b4b2ca53cfb3b678afae7
  'zaloJSV2 is not defined',
  // https://padlet-y9.sentry.io/share/issue/8e1f8a66f4d24728b3883b9eac12ee20
  'Failed to fetch',
  // https://padlet-y9.sentry.io/share/issue/92fd4eafe229462f9b4df8858cdc8895/
  'AbortError',
  // If network is spotty, there's nothing we can do with this error
  // https://padlet-y9.sentry.io/share/issue/616672cc0fe9440c9a830c8484088a98/
  'UploadError',
  'UploadError: UploadError',
  // https://padlet-y9.sentry.io/share/issue/54c1e64416ad421fbaa3043f42502a61/
  'FetchLinkError',
  // https://sentry.io/answers/load-failed-javascript/
  'Load failed',
]

/**
 * Get the tunnel URL for the current environment
 * @returns The tunnel URL
 */
function getTunnelUrl(environment: string): string {
  const apiKeyBase64 = btoa(API_KEY)
  const path = `/cgi-bin/report-error/${apiKeyBase64}`
  if (environment === PROD_ENV) {
    return path
  } else {
    return `https://padlet.com${path}`
  }
}

function isFrameCaptureFunction(frame: StackFrame): boolean {
  const frameFunction = frame.function
  if (!frameFunction) return false
  return frameFunction.includes('captureMessage') || frameFunction.includes('captureException')
}

// Turn this on if sentry breadcrumbs are being unhelpful.
// function beforeBreadcrumb (breadcrumb: Breadcrumb): Breadcrumb|null {
//   return breadcrumb.category === 'sentry' ? null : breadcrumb
// }

function beforeSend(event: Event, hint?: EventHint): Event | null {
  if (browserIs('ie')) return null
  // Drop noisy `UnhandledRejection` exceptions for non-sandbox pages (legacy behaviour).
  // The Sandbox page (`source === 'sandbox'`) explicitly wants every unhandled rejection
  // captured to Sentry — see PRO-30444. The global scope tag set during init when
  // `source: 'sandbox'` is passed is merged into `event.tags` before `beforeSend` runs,
  // so this check is reliable.
  const isSandboxEvent = event.tags?.source === 'sandbox'
  if (
    !isSandboxEvent &&
    event?.exception?.values != null &&
    event?.exception?.values[0].type === 'UnhandledRejection'
  ) {
    return null
  }

  // Flag capturing-related function that happens in this file instead of the app as in_app=false
  // Sentry logs will look better, showing the correct file/function the error is actually on
  if (event.exception?.values != null) {
    const frames = event.exception?.values[0].stacktrace?.frames
    if (frames != null && frames.filter(isFrameCaptureFunction).length > 0) {
      let lastFrameIndex = frames.length - 1
      // Flag all frames after the capture function call, inclusive
      do {
        frames[lastFrameIndex].in_app = false
        lastFrameIndex -= 1
        if (isFrameCaptureFunction(frames[lastFrameIndex]) || lastFrameIndex === 0) break
      } while (true)
    }
  }

  // Fix issue #1504 in github.com/padlet/mozart
  // Only allow this event through on the "staging" environment.
  if (event.environment !== STAGING_ENV && hint != null && hint.originalException) {
    let message = ''
    if (typeof hint.originalException === 'string') {
      message = hint.originalException
    } else {
      const errorMessage = (hint.originalException as any).message
      if (errorMessage) message = errorMessage
    }

    if (message && event.tags?.network_failure !== 'true') {
      let excludeMessage = false
      // I know for loop might be better here but typescript doesn't like loops
      messagesToExclude.forEach((messageToExclude): void => {
        if (!excludeMessage && message.startsWith(messageToExclude)) {
          excludeMessage = true
        }
      })
      if (excludeMessage) return null
    }
  }
  return event
}

function setScope(options: ScopeOptions = {}): void {
  configureScope((scope): void => {
    for (const key in options) {
      if (key == 'userId') {
        scope.setUser({ id: options?.userId?.toString() })
      } else {
        scope.setTag(key, options[key])
      }
    }
  })
}

function init(options: InitOptions): void {
  const skipSentry = window.location.protocol === 'file:'
  if (skipSentry) {
    console.error('Skipping Sentry initialization')
    return
  }

  const environment = options.environment ?? process?.env?.NODE_ENV ?? DEV_ENV
  const release = options.release ?? 'unknown'
  const source = options.source ?? (isSandboxPage() ? SANDBOX_SOURCE_TAG_VALUE : undefined)

  const sentryOptions: BrowserOptions = {
    dsn: API_KEY,
    environment,
    release,
    defaultIntegrations: false,
    beforeSend,
    ignoreErrors,
    denyUrls,
    allowUrls,
    tunnel: getTunnelUrl(environment),
  }

  // This is the default, but we are keeping the list here to be more aware of what's happening.
  const integrations: any[] = [
    new Integrations.InboundFilters(),
    new Integrations.FunctionToString(),
    new Integrations.TryCatch(),
    new Integrations.Breadcrumbs({ sentry: false }),
    new Integrations.GlobalHandlers({
      onerror: true,
      // Always capture `unhandledrejection` so the Sandbox can report unhandled promise
      // failures to Sentry (PRO-30444). For non-sandbox pages the resulting events are
      // dropped in `beforeSend` to preserve the previous prod-only behaviour.
      onunhandledrejection: true,
    }),
    new Integrations.LinkedErrors(),
    new Integrations.HttpContext(),
    new ExtraErrorData(),
    new Dedupe(),
  ]

  // Only the Sandbox routes `console.error` calls to Sentry (PRO-30444). Other
  // pages keep the legacy behaviour to avoid a flood of low-signal events.
  if (source === SANDBOX_SOURCE_TAG_VALUE) {
    integrations.push(new CaptureConsole({ levels: ['error'] }))
  }

  sentryOptions.integrations = integrations

  const vueOptions = {
    Vue: options.vue,
    attachProps: true,
    logErrors: true, // let error surface to console
  }

  if (ENABLED) {
    sentryInit({ ...sentryOptions, ...vueOptions })
    setScope({
      cookies: browserCan('cookies') ? 'yes' : 'no',
      ...(options.userId && { userId: options.userId }),
      ...(options.locale && { locale: options.locale }),
      // Setting `source` here ensures the global scope tag is in place before
      // any subsequent capture (handled or unhandled) is sent to Sentry.
      ...(source != null && { source }),
    })
  }
}

function captureException(e: any, options: CaptureOptions = {}): void {
  console.error(e)
  if (ENABLED) {
    withScope((scope): void => {
      if (options.context != null) {
        Object.keys(options.context).forEach((key): void => {
          scope.setExtra(key, options.context[key])
        })
      }
      if (options.context?.outcome != null && options.tags?.outcome == null) {
        scope.setTag('outcome', options.context.outcome)
      }
      if (options.tags != null) {
        Object.entries(options.tags).forEach(([key, value]): void => {
          scope.setTag(key, value)
        })
      }
      if (options.fingerprint != null) {
        scope.setFingerprint(options.fingerprint)
      }
      sentryCaptureException(e)
    })
  }
}

function toTitleCase(str: string): string {
  return str.slice(0, 1).toUpperCase() + str.slice(1, str.length)
}

/**
 * Helper function to get error name for tracking on Sentry
 * from generic error message
 * @param message
 * @return name for Error class
 * @example getErrorName("Fail to fetch link") // "FailToFetchError"
 */
function getErrorName(message: string): string {
  const words = message.split(' ')
  const MAX_ERROR_NAME_LENGTH = 8
  let errorName = ''
  for (let i = 0; i < words.length; i++) {
    errorName = errorName + toTitleCase(words[i])
    if (errorName.length >= MAX_ERROR_NAME_LENGTH) {
      break
    }
  }
  if (!errorName.includes('Error')) {
    errorName = errorName + 'Error'
  }
  return errorName
}

/**
 * Create Error object with a class name
 * @param name - class name (e.g. CreatedPostError)
 * @param message
 * @return error object
 * @example createNamedError("FailToFetchError", "Fail to fetch link for image attachment") // returns FailToFetchError that is a Error object
 */
function createNamedError(name: string, message: string): Error {
  const instance = new Error(message)
  instance.name = name
  return instance
}

/** Capture generic message as Error on sentry */
function captureMessage(message: string, options: CaptureOptions = {}): void {
  console.warn(message)
  if (ENABLED) {
    const errorName = getErrorName(message)
    const captureOptions = options.networkFailure
      ? buildNetworkFailureCaptureOptions(
          options.context ?? {},
          options.source ?? 'unknown',
          options.context?.outcome ?? 'unknown',
          false,
        )
      : options
    withScope((scope): void => {
      if (captureOptions.context != null) {
        Object.keys(captureOptions.context).forEach((key): void => {
          scope.setExtra(key, captureOptions.context[key])
        })
      }
      if (captureOptions.context?.outcome != null && captureOptions.tags?.outcome == null) {
        scope.setTag('outcome', captureOptions.context.outcome)
      }
      if (captureOptions.tags != null) {
        Object.entries(captureOptions.tags).forEach(([key, value]): void => {
          scope.setTag(key, value)
        })
      }
      if (captureOptions.fingerprint != null) {
        scope.setFingerprint(captureOptions.fingerprint)
      }
      sentryCaptureException(createNamedError(errorName, message))
    })
  }
}

// See @padlet/fetch.
function isNetworkFetchError(error: unknown): boolean {
  if (error == null || typeof error !== 'object') return false
  const fetchError = error as Partial<FetchError>
  return fetchError.originalError != null || fetchError.status === 0
}

function recordNetworkFailureWithinBudget(source: string): boolean {
  const sourceCount = networkFailureCountsBySource.get(source) ?? 0
  if (sourceCount >= MAX_NETWORK_FAILURES_PER_SOURCE || networkFailureCount >= MAX_NETWORK_FAILURES_PER_SESSION) {
    return false
  }
  networkFailureCountsBySource.set(source, sourceCount + 1)
  networkFailureCount += 1
  return true
}

function recordRecoveryWithinBudget(source: string): boolean {
  const sourceCount = recoveryCountsBySource.get(source) ?? 0
  if (sourceCount >= MAX_RECOVERIES_PER_SOURCE || recoveryCount >= MAX_RECOVERIES_PER_SESSION) {
    return false
  }
  recoveryCountsBySource.set(source, sourceCount + 1)
  recoveryCount += 1
  return true
}

function buildNetworkFailureCaptureOptions(
  context: { [key: string]: any },
  source: string,
  outcome: any,
  sampled: boolean,
): CaptureOptions {
  const effectiveType = networkMonitor.getEffectiveType()
  const online = networkMonitor.isOnline()
  return {
    context: {
      ...context,
      effectiveType,
      rtt: networkMonitor.getRtt(),
      downlink: networkMonitor.getDownlink(),
      saveData: networkMonitor.isSaveDataEnabled(),
      online,
    },
    tags: {
      network_failure: 'true',
      feature: 'network_fetch',
      fetch_source: source,
      effective_type: effectiveType ?? 'unknown',
      online: String(online),
      outcome,
      retry_attempt: context.retry_attempt ?? '1',
      sampled: String(sampled),
      sample_rate: String(NETWORK_FAILURE_SESSION_SAMPLE_RATE),
    },
    fingerprint: ['network-fetch-failure', source, outcome],
  }
}

type CaptureFetchExceptionOptions = {
  treatAsNetworkFailure?: boolean
}

function captureFetchException(
  e: unknown,
  extraContext?: { [key: string]: any },
  options?: CaptureFetchExceptionOptions,
): void {
  const fetchError = e != null && typeof e === 'object' ? (e as Partial<FetchError>) : {}
  const contextExtra = extraContext ?? {}
  const outcome = contextExtra.outcome ?? 'unknown'
  const context = {
    status: fetchError.status,
    error: fetchError.originalError,
    online: window?.navigator?.onLine,
    ...contextExtra,
  }
  const isNetworkFailure = isNetworkFetchError(e) || options?.treatAsNetworkFailure === true
  const usesNetworkBudget = isNetworkFailure || contextExtra.outcome === 'retry_succeeded'
  if (!usesNetworkBudget) {
    captureException(e, { context, tags: { outcome } })
    return
  }

  if (!isNetworkFailureSampledSession) return

  const source = contextExtra.source ?? 'unknown'
  if (contextExtra.outcome === 'retry_succeeded') {
    if (!recordRecoveryWithinBudget(source)) return
  } else {
    if (!recordNetworkFailureWithinBudget(source)) return
  }

  if (!isNetworkFailure) {
    captureException(e, { context, tags: { outcome } })
    return
  }

  captureException(e, buildNetworkFailureCaptureOptions(context, source, outcome, true))
}

function trackFailedIcons(delayAfterPageLoad = 10000): void {
  try {
    const isLigatureIcon = (node): boolean => {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (context == null) {
        return true
      }
      const word = node.textContent
      if (!word) return false

      const font = `24px "immaterial"`

      context.font = font
      const size = context.measureText(word)
      return size.width < 30
    }
    setTimeout(() => {
      const iNodes = Array.from(document.querySelectorAll('i.immaterial-icons'))
      const failedIcons = iNodes.filter((node) => !isLigatureIcon(node))
      const numberOfFailedIcons = failedIcons.length
      if (numberOfFailedIcons > 0) {
        captureMessage(`Failed to load immaterial icons on page`, {
          context: { first: failedIcons[0].outerHTML, count: `${numberOfFailedIcons}/${iNodes.length}` },
        })
      }
    }, delayAfterPageLoad)
  } catch (_err) {
    captureMessage(`Cannot test icons to track failed icons`)
  }
}

export {
  captureException,
  captureFetchException,
  captureMessage,
  init,
  isNetworkFetchError,
  SANDBOX_SOURCE_TAG_VALUE,
  setScope,
  trackFailedIcons,
}
